// ABOUTME: localStorage snapshot of a user's admin/beta gate results.
// ABOUTME: Lets repeat visits paint before the Firestore gate checks resolve.

const KEY_PREFIX = "auth_gate_cache_";
const TTL_MS = 24 * 60 * 60 * 1000;

export interface AuthGateSnapshot {
  isAdmin: boolean;
  betaApproved: boolean;
}

interface StoredSnapshot extends AuthGateSnapshot {
  savedAt: number;
}

const key = (uid: string) => `${KEY_PREFIX}${uid}`;

/**
 * Returns the cached gate result for a user, or null if absent/expired/corrupt.
 *
 * This is a rendering fast-path, NOT a security boundary. Firestore rules remain
 * the enforcement point for both admin access and per-user data — a stale or
 * tampered snapshot can only change which UI affordances appear, never what the
 * server will hand over. Every cache hit is revalidated against Firestore in the
 * background, and a revoked user is signed out when that lands.
 */
export function readAuthGate(uid: string): AuthGateSnapshot | null {
  try {
    const raw = localStorage.getItem(key(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSnapshot;
    if (
      typeof parsed?.isAdmin !== "boolean" ||
      typeof parsed?.betaApproved !== "boolean" ||
      typeof parsed?.savedAt !== "number"
    ) {
      return null;
    }
    if (Date.now() - parsed.savedAt > TTL_MS) {
      clearAuthGate(uid);
      return null;
    }
    return { isAdmin: parsed.isAdmin, betaApproved: parsed.betaApproved };
  } catch {
    clearAuthGate(uid);
    return null;
  }
}

export function writeAuthGate(uid: string, snapshot: AuthGateSnapshot): void {
  try {
    const payload: StoredSnapshot = { ...snapshot, savedAt: Date.now() };
    localStorage.setItem(key(uid), JSON.stringify(payload));
  } catch {
    // localStorage may be full or unavailable — the cold path still works.
  }
}

export function clearAuthGate(uid: string): void {
  try {
    localStorage.removeItem(key(uid));
  } catch {
    // Ignore.
  }
}
